/**
 * POSummaryPane — Quick-access PO list inside the OrderPlacementModal.
 *
 * Surfaces the POs generated from this packet (one per vendor). Each PO row is
 * clickable to open the full PurchaseOrderDetail in a nested dialog. POs are
 * auto-generated when the packet is marked Placed; reps can also generate
 * early via the "Generate POs" button (e.g. to email PDFs to vendors before
 * placing).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, FileText, Plus, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { PO_STATUS_LABEL, PO_STATUS_TONE, type OrderingPacket } from "@/data/orderingPacket/types";
import { PurchaseOrderDetail } from "@/components/finance/PurchaseOrderDetail";
import { supabase } from "@/lib/supabase";
import { useRole } from "@/data/RoleContext";
import { POAttachments } from "@/components/ordering/POAttachments";

const CAN_UPLOAD_PROOFS = new Set([
  "super_admin", "admin", "owner",
  "warehouse_manager", "ops_manager", "procurement_manager",
]);

const TONE_BADGE: Record<string, string> = {
  muted: "bg-muted text-muted-foreground border-border",
  info: "bg-info/15 text-info border-info/30",
  warning: "bg-warning/15 text-warning border-warning/30",
  success: "bg-success/15 text-success border-success/30",
  destructive: "bg-destructive/15 text-destructive border-destructive/30",
};

interface Props {
  packet: OrderingPacket;
}

export function POSummaryPane({ packet }: Props) {
  const { generatePOsForPacket } = useOrderingPacket();
  const { role } = useRole();
  const canUpload = CAN_UPLOAD_PROOFS.has(role ?? "");
  const [openPOId, setOpenPOId] = useState<string | null>(null);
  const [poTaxOverrides, setPoTaxOverrides] = useState<Record<string, number>>({});
  const pos = packet.purchaseOrders ?? [];

  async function handleTaxChange(poId: string, tax: number) {
    setPoTaxOverrides((prev) => ({ ...prev, [poId]: tax }));
    const po = pos.find((p) => p.id === poId);
    if (!po) return;
    const newTotal = po.subtotal + tax;
    await supabase
      .from("purchase_orders")
      .update({ tax, total_cost: newTotal })
      .eq("id", poId);
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[12px] font-bold uppercase tracking-wider flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Purchase Orders ({pos.length})
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            One PO per vendor. POs are auto-generated when you mark the packet placed —
            or generate early to email vendor confirmations.
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => generatePOsForPacket(packet.id)}>
          <Plus className="h-3 w-3 mr-1" />
          {pos.length === 0 ? "Generate POs" : "Refresh from lines"}
        </Button>
      </div>

      {pos.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <FileText className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-[12px] text-muted-foreground">
            No purchase orders yet.
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">
            POs are created automatically when you mark the packet placed, grouped by vendor.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {pos.map((po) => {
            const tone = PO_STATUS_TONE[po.status];
            const lineCount = packet.lineItems.filter((l) => l.poId === po.id).length;
            const invoiced = po.invoices.reduce((s, i) => s + i.invoiceAmount, 0);
            const unpaid = po.invoices.filter((i) => !i.paid).reduce((s, i) => s + i.invoiceAmount, 0);
            const variance = po.invoices.length === 0 ? 0 : +(invoiced - po.subtotal).toFixed(2);

            return (
              <Card
                key={po.id}
                className="p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setOpenPOId(po.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[13px] font-bold">{po.poNumber}</span>
                      <Badge className={cn("text-[10px] font-semibold border", TONE_BADGE[tone])}>
                        {PO_STATUS_LABEL[po.status]}
                      </Badge>
                      {variance !== 0 && (
                        <Badge className={cn("text-[10px] font-semibold border tabular-nums", TONE_BADGE.warning)}>
                          <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                          Variance {variance > 0 ? "+" : ""}${variance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </Badge>
                      )}
                      {po.invoices.length > 0 && variance === 0 && (
                        <Badge className={cn("text-[10px] font-semibold border", TONE_BADGE.success)}>
                          <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> 3-way match
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5">
                      <Building2 className="h-3 w-3" />
                      <span className="font-medium text-foreground">{po.vendorName}</span>
                      {po.vendorOrderNumber && <span className="font-mono">· SO# {po.vendorOrderNumber}</span>}
                      <span>· {lineCount} line{lineCount === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <div className="text-right space-y-1" onClick={(e) => e.stopPropagation()}>
                    <div className="text-[11px] text-muted-foreground tabular-nums">
                      Subtotal ${po.subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                    <div className="flex items-center justify-end gap-1.5 text-[11px]">
                      <label className="text-muted-foreground" htmlFor={`po-tax-${po.id}`}>Tax $</label>
                      <input
                        id={`po-tax-${po.id}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={poTaxOverrides[po.id] ?? ""}
                        placeholder="0.00"
                        onChange={(e) => void handleTaxChange(po.id, Math.max(0, parseFloat(e.target.value) || 0))}
                        className="w-20 h-6 text-right text-[11px] tabular-nums border border-input rounded px-2 bg-background"
                      />
                    </div>
                    <div className="text-[13px] font-bold tabular-nums">
                      ${(po.subtotal + (poTaxOverrides[po.id] ?? 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                    {po.invoices.length > 0 && (
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        Invoiced ${invoiced.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        {unpaid > 0 && <span className="text-warning"> · ${unpaid.toLocaleString(undefined, { maximumFractionDigits: 2 })} unpaid</span>}
                      </div>
                    )}
                    <ExternalLink className="h-3 w-3 inline text-muted-foreground mt-1" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Nested PO detail dialog */}
      <Dialog open={!!openPOId} onOpenChange={(o) => !o && setOpenPOId(null)}>
        <DialogContent className="max-w-[1100px] w-[95vw] h-[90vh] max-h-[90vh] p-0 gap-0 overflow-hidden grid grid-rows-[auto_minmax(0,1fr)]">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
            <DialogTitle className="text-[16px] font-bold">Purchase Order Detail</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            {openPOId && <PurchaseOrderDetail poId={openPOId} onClose={() => setOpenPOId(null)} />}
            {openPOId && (
              <div className="border-t border-border/50 mt-4 pt-4 px-6 pb-6">
                <POAttachments poId={openPOId} canUpload={canUpload} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
